import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Building2, Trash2 } from "lucide-react";

export default function Dealerships() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "" });

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/dealerships");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      await api.post("/dealerships", payload);
      setShowCreate(false);
      setForm({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "" });
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dealership?")) return;
    await api.delete(`/dealerships/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-12" data-testid="dealerships-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Clients</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Dealerships</h1>
        </div>
        {isAdmin && (
          <button data-testid="dealerships-new-btn" onClick={() => setShowCreate(true)} className="bg-black text-white px-5 py-3 hover:bg-[#FF2B00] label-caps inline-flex items-center gap-2">
            <Plus size={16} /> Add Dealership
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {items.length === 0 && <div className="bg-white p-12 text-center text-zinc-500 col-span-full" data-testid="dealerships-empty">No dealerships yet.</div>}
        {items.map((d) => (
          <div key={d.id} className="bg-white p-6 group" data-testid={`dealership-card-${d.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <Building2 size={18} className="text-[#002FA7]" />
                <div className="font-display text-xl mt-3">{d.name}</div>
                {d.contact_name && <div className="text-sm text-zinc-600 mt-1">{d.contact_name}</div>}
                {d.contact_email && <div className="text-xs text-zinc-400">{d.contact_email}</div>}
                {d.contact_phone && <div className="text-xs text-zinc-400">{d.contact_phone}</div>}
                {d.address && <div className="text-xs text-zinc-500 mt-2">{d.address}</div>}
              </div>
              {isAdmin && (
                <button data-testid={`dealer-delete-${d.id}`} onClick={() => remove(d.id)} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-[#FF2B00]"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="dealer-create-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display text-2xl">Add Dealership</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div><label className="label-caps block mb-1">Name</label>
                <input data-testid="dealer-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Contact Name</label>
                <input data-testid="dealer-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Contact Email</label>
                <input data-testid="dealer-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Contact Phone</label>
                <input data-testid="dealer-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Address</label>
                <input data-testid="dealer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              {error && <div className="text-sm text-[#FF2B00]">{error}</div>}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-black px-5 py-2 label-caps">Cancel</button>
                <button data-testid="dealer-create-submit" type="submit" className="bg-black text-white px-5 py-2 hover:bg-[#FF2B00] label-caps">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
